import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import Layout from '../Layout'
import Pagination from '../../../components/Pagination'
import { useLocale } from '../../../lib/LocaleContext'

// Owner-code sanitizer — pure function (Detroit unit tests).
// Accepts both a 6-digit TOTP code and a backup code (format XXXXX-XXXXX,
// see Otp::Verifier#backup_code_format?). Deliberately does NOT strip
// non-digit characters (unlike TwoFactor.jsx's disable/regen inputs) —
// that would make backup codes impossible to type. D4 in the design.
export function sanitizeOwnerCode(raw) {
  return raw.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 11)
}

// Client-side validation mirroring Otp::Verifier's two accepted shapes:
// a 6-digit TOTP code, or a backup code XXXXX-XXXXX.
export function isValidOwnerCode(code) {
  return /^\d{6}$/.test(code) || /^[A-Z0-9]{5}-[A-Z0-9]{5}$/.test(code)
}

function RoleBadge({ role }) {
  const isOwner = role === 'owner'
  return (
    <span
      className={[
        'inline-block px-2 py-0.5 rounded text-xs font-mono font-semibold',
        isOwner
          ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-800'
          : 'bg-zinc-800 text-zinc-400 border border-zinc-700',
      ].join(' ')}
    >
      {role}
    </span>
  )
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function Index({ members = [], reveal = null, pagination = {}, workspaces = [] }) {
  const [email, setEmail] = useState('')
  const [workspaceId, setWorkspaceId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { t } = useLocale()
  const { props } = usePage()
  const flash = props?.flash || {}

  const [reset2faTarget, setReset2faTarget] = useState(null)
  const [ownerCode, setOwnerCode] = useState('')
  const [reset2faError, setReset2faError] = useState(null)
  const [reset2faSubmitting, setReset2faSubmitting] = useState(false)

  const ownerCount = members.filter(m => m.role === 'owner').length

  function isLastOwner(member) {
    return member.is_current_user && member.role === 'owner' && ownerCount === 1
  }

  function handleCreate(e) {
    e.preventDefault()
    if (!email.trim() || !workspaceId) return
    setSubmitting(true)
    router.post(
      '/settings/users',
      { email: email.trim(), workspace_id: workspaceId },
      {
        onFinish: () => {
          setSubmitting(false)
          setEmail('')
          setWorkspaceId('')
        },
      }
    )
  }

  function handleRoleChange(member) {
    const newRole = member.role === 'owner' ? 'member' : 'owner'
    router.patch(`/settings/users/${member.id}`, { role: newRole })
  }

  function handleDelete(member) {
    if (!confirm(t('settings.users.confirm_remove'))) return
    router.delete(`/settings/users/${member.id}`)
  }

  function handleResend(member) {
    router.post(`/settings/users/${member.id}/resend_credentials`)
  }

  function handleReset2FA(member) {
    setReset2faTarget(member)
    setOwnerCode('')
    setReset2faError(null)
  }

  function cancelReset2FA() {
    setReset2faTarget(null)
    setOwnerCode('')
    setReset2faError(null)
  }

  function submitReset2FA(e) {
    e.preventDefault()
    if (!ownerCode) {
      setReset2faError('required')
      return
    }
    if (!isValidOwnerCode(ownerCode)) {
      setReset2faError('malformed')
      return
    }
    setReset2faError(null)
    setReset2faSubmitting(true)
    router.delete(`/settings/users/${reset2faTarget.id}/destroy_two_factor`, {
      data: { owner_code: ownerCode },
      onFinish: () => {
        setReset2faSubmitting(false)
        setReset2faTarget(null)
        setOwnerCode('')
      },
    })
  }

  return (
    <Layout active="users">
      <div className="max-w-3xl space-y-6">
        {flash.notice && (
          <div
            data-testid="flash-notice"
            role="status"
            aria-label={t('settings.users.reset_2fa_panel.banner_success_label')}
            className="px-4 py-2 bg-emerald-950 border border-emerald-800 rounded-lg text-sm text-emerald-400 font-mono"
          >
            {flash.notice}
          </div>
        )}
        {flash.alert && (
          <div
            data-testid="flash-alert"
            role="alert"
            aria-label={t('settings.users.reset_2fa_panel.banner_error_label')}
            className="px-4 py-2 bg-red-950 border border-red-800 rounded-lg text-sm text-red-400 font-mono"
          >
            {flash.alert}
          </div>
        )}

        <div>
          <h2 className="text-lg font-mono font-bold text-zinc-100">{t('settings.users.title')}</h2>
          <p className="text-sm text-zinc-500 mt-1">
            {t('settings.users.subtitle')}
          </p>
        </div>

        {/* Reset 2FA confirmation panel — replaces window.confirm; collects the
            acting admin's own owner code before dispatch (D3). */}
        {reset2faTarget && (
          <section className="border border-amber-800/50 bg-amber-950/20 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-mono font-semibold text-amber-300">
              {t('settings.users.reset_2fa_panel.title', { email: reset2faTarget.email })}
            </h3>
            <p className="text-xs text-zinc-400">
              {t('settings.users.reset_2fa_panel.instruction')}
            </p>
            <form onSubmit={submitReset2FA} className="space-y-2">
              <label className="block text-xs font-mono text-zinc-400" htmlFor="reset-2fa-owner-code">
                {t('settings.users.reset_2fa_panel.owner_code_label')}
              </label>
              <input
                id="reset-2fa-owner-code"
                type="text"
                value={ownerCode}
                onChange={e => setOwnerCode(sanitizeOwnerCode(e.target.value))}
                placeholder={t('settings.users.reset_2fa_panel.owner_code_placeholder')}
                maxLength={11}
                autoFocus
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600 tracking-widest focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-600"
              />
              {reset2faError && (
                <p className="text-xs font-mono text-red-400">
                  {reset2faError === 'required'
                    ? t('settings.users.reset_2fa_panel.error_required')
                    : t('settings.users.reset_2fa_panel.error_malformed')}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={reset2faSubmitting}
                  className="bg-amber-900/60 hover:bg-amber-800/80 disabled:opacity-50 disabled:cursor-not-allowed text-amber-200 border border-amber-800/40 font-mono text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  {reset2faSubmitting
                    ? t('settings.users.reset_2fa_panel.submitting')
                    : t('settings.users.reset_2fa_panel.submit')}
                </button>
                <button
                  type="button"
                  onClick={cancelReset2FA}
                  className="px-4 py-2 text-zinc-400 hover:text-zinc-200 font-mono text-xs rounded-lg border border-zinc-700 transition-colors"
                >
                  {t('settings.users.reset_2fa_panel.cancel')}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* Create user form */}
        <section className="border border-zinc-700 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-mono font-semibold text-zinc-300">{t('settings.users.create_title')}</h3>
          <form onSubmit={handleCreate} className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('settings.users.email_placeholder')}
              required
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
            />
            <select
              value={workspaceId}
              onChange={e => setWorkspaceId(e.target.value)}
              required
              className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600"
            >
              <option value="" disabled>{t('settings.users.select_workspace')}</option>
              {workspaces.map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={submitting || !workspaceId}
              className="bg-emerald-800/60 hover:bg-emerald-700/80 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-200 border border-emerald-700/40 font-mono text-sm font-medium px-4 py-2 rounded-lg transition-colors shrink-0"
            >
              {submitting ? t('settings.users.creating') : t('settings.users.create_submit')}
            </button>
          </form>
        </section>

        {/* Members table */}
        <section className="border border-zinc-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm font-mono">
            <thead>
              <tr className="border-b border-zinc-700 bg-zinc-800/40">
                <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                  {t('settings.users.table.email')}
                </th>
                <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                  {t('settings.users.table.role')}
                </th>
                <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                  {t('settings.users.table.joined')}
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-700">
              {members.map(member => {

                const locked = isLastOwner(member)
                return (
                  <tr key={member.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-3 text-zinc-200">
                      {member.email}
                      {member.is_current_user && (
                        <span className="ml-2 text-xs text-zinc-600">({t('common.you')})</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={member.role} />
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      {formatDate(member.joined_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleRoleChange(member)}
                          disabled={locked}
                          title={locked ? t('settings.users.tooltips.sole_owner_role') : `Change to ${member.role === 'owner' ? 'member' : 'owner'}`}
                          className="px-2 py-1 rounded text-xs text-zinc-400 bg-zinc-800 hover:bg-zinc-700 hover:text-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {member.role === 'owner' ? t('settings.users.actions.to_member') : t('settings.users.actions.to_owner')}
                        </button>
                        <button
                          onClick={() => handleResend(member)}
                          title={t('settings.users.tooltips.reset_pwd')}
                          className="px-2 py-1 rounded text-xs text-zinc-400 bg-zinc-800 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
                        >
                          {t('settings.users.actions.reset_pwd')}
                        </button>
                        <button
                          onClick={() => handleReset2FA(member)}
                          disabled={member.is_current_user}
                          title={member.is_current_user ? t('settings.users.tooltips.reset_2fa_self') : t('settings.users.tooltips.reset_2fa')}
                          className="px-2 py-1 rounded text-xs text-amber-500 bg-zinc-800 hover:bg-amber-950 hover:text-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {t('settings.users.actions.reset_2fa')}
                        </button>
                        <button
                          onClick={() => handleDelete(member)}
                          disabled={locked}
                          title={locked ? t('settings.users.tooltips.sole_owner_remove') : t('settings.users.actions.remove')}
                          className="px-2 py-1 rounded text-xs text-red-500 bg-zinc-800 hover:bg-red-950 hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {t('settings.users.actions.remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {members.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-zinc-600">
                    {t('settings.users.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <Pagination
          current_page={pagination.current_page}
          total_pages={pagination.total_pages}
          onPageChange={page => router.get('/settings/users', { page }, { preserveState: true })}
        />
      </div>
    </Layout>
  )
}
